import { Col, Dropdown, message, Row } from "antd";
import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import Button from "@components/Button";
import Table from "@components/Table";
import {
  ErrorServices,
  useFetchLongTermIncentivesQuery,
  useDeleteLongTermIncentiveMutation,
  useUploadLongTermIncentivesMutation,
  longTermIPApi,
  useFetchLongTermIncentivesByPlanQuery,
} from "@services";
import { getColumns } from "./config";
import { ILongTermIncentive } from "@types";
import AddLongTermIncentive from "./AddIncentive";
import LongTermIncentiveVersions from "./Versions";
import Upload from "@components/Upload";
import { useTypedSelector } from "@hooks";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import CustomButton from "@/components/customButton";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import SearchBox from "@/components/searchInput";
import customErrorMessage from "@/components/CustomErrorMessage";
import UploadButton from "@/components/UploadButton";
import { IoFilterSharp } from "react-icons/io5";
import useDebounce from "@/hooks/useDebounce";

const LongTermIncentives = () => {
  const { company, role } = useTypedSelector(state => state.auth?.user!);
  const { plan_id } = useParams<{ plan_id?: string }>();
  const { id: company_id } = company!;
  const dispatch = useDispatch();
  const longTermIncentive_id = useRef<any>(null);
  const [selectedLongTermIncentive, setSelectedLongTermIncentive] =
    useState<ILongTermIncentive | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isVersionsModal, setIsVersionsModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [isVisibleForEdit, setIsVisibleForEdit] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);

  const [deleteLongTermIncentive, { isLoading: isDeleting }] =
    useDeleteLongTermIncentiveMutation();
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState({
    file: null,
    active: false,
  });
  const [uploadLongTermIncentives, { isLoading: isUploading }] =
    useUploadLongTermIncentivesMutation();
  const {
    data: incentivesData,
    isLoading,
    refetch,
  } = useFetchLongTermIncentivesQuery(
    {
      company_id,
      page,
      page_size: pageSize,
      sort_by: sortBy,
      order: order,
      search: debouncedSearch,
    },
    { skip: !!plan_id === true }
  );
  const { data: incentives, pagination: incentivesPagination } =
    incentivesData || {};
  const { data: incentivesByPlanData, isLoading: isFetchingPlanIncentives } =
    useFetchLongTermIncentivesByPlanQuery(
      {
        company_id,
        plan_id,
        page,
      },
      { skip: !!plan_id === false }
    );
  const { data: incentivesByPlan, pagination: incentivesByPlanPagination } =
    incentivesByPlanData || {};

  const data = plan_id ? incentivesByPlan : incentives;
  const pagination = plan_id
    ? incentivesByPlanPagination
    : incentivesPagination;

  const editLongTermIncentive = (incentive: ILongTermIncentive) => {
    setSelectedLongTermIncentive(incentive);
    setIsVisibleForEdit(true);
  };

  const removeLongTermIncentive = async (id: number) => {
    try {
      longTermIncentive_id.current = id;
      await deleteLongTermIncentive({ company_id, id });
      message.success("Long term incentive deleted successfully!");
    } catch (error) {
      ErrorServices(error);
    }
  };

  const handleUpload = async () => {
    const { file, active }: { file: any; active: boolean } = uploadFile;
    setIsUploadModal(false);
    try {
      const formData = new FormData();
      formData.append("attachment", file!, file?.name!);
      const res = await uploadLongTermIncentives({
        company_id,
        active,
        body: formData,
      }).unwrap();
      message.success(res?.message);
    } catch (error) {
      customErrorMessage(error?.message);
    }
  };
  useEffect(() => {
    refetch();
  }, []);

  const download = (id?: number) => {
    dispatch(
      longTermIPApi.endpoints.downloadLongTermIncentives.initiate(
        {
          company_id,
          version_id: id || null,
        },
        {
          subscribe: false,
          forceRefetch: true,
        }
      )
    );
  };

  const columns = getColumns({
    isDeleting,
    longTermIncentive_id,
    removeLongTermIncentive,
    editLongTermIncentive,
    userRole: role,
  });

  // ✅ COLUMN STATE
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    columns.map(col => col.key)
  );

  // ✅ FILTERED COLUMNS
  const filteredColumns = columns.filter(col =>
    visibleColumnKeys.includes(col.key)
  );

  // custom overlay
  const columnOverlay = (
    <div
      style={{
        background: "#fff",
        padding: 8,
        borderRadius: 6,
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
        minWidth: 240,
        maxHeight: 300,
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((col) => {
        const isActionsColumn = col.key === "actions";
        const isChecked = visibleColumnKeys.includes(col.key);
        const isDisabled =
          isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

        return (
          <label
            key={col.key}
            style={{
               display: isDisabled ? "none" : "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (isActionsColumn) return;

                if (isChecked) {
                  setVisibleColumnKeys((prev) =>
                    prev.filter((key) => key !== col.key)
                  );
                } else {
                  setVisibleColumnKeys((prev) => [...prev, col.key]);
                }
              }}
              style={{
                margin: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typeof col.title === "string"
                ? col.title.replace(/\b\w/g, c => c.toUpperCase())
                : col.title}
            </span>
          </label>
        );
      })}
    </div>
  );

  return (
    <>
      {isUploadModal && (
        <Upload
          title="Upload Long Term Incentives"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}
      {isVisible && (
        <AddLongTermIncentive
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          selectedLongTermIncentive={selectedLongTermIncentive}
          setSelectedLongTermIncentive={setSelectedLongTermIncentive}
        />
      )}
      {isVisibleForEdit && selectedLongTermIncentive?.id && (
        <AddLongTermIncentive
          isVisible={isVisibleForEdit}
          setIsVisible={() => {
            setIsVisibleForEdit(false);
            setSelectedLongTermIncentive(null);
          }}
          selectedLongTermIncentive={selectedLongTermIncentive}
          setSelectedLongTermIncentive={setSelectedLongTermIncentive}
          isEdit={true}
        />
      )}
      {isVersionsModal && (
        <LongTermIncentiveVersions
          isVisible={isVersionsModal}
          setIsVisible={setIsVersionsModal}
          download={download}
        />
      )}

      <div className="bg-white p-4 sm:p-5">
        <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-white  font-medium text-3xl uppercase">
            Long Term Incentives
          </div>

          <div className="flex flex-row justify-between">
            {!plan_id ? (
              <>
                <UploadButton
                  role={role}
                  createPermission={permissions.CREATE_COMPANY_LTI}
                  updatePermission={permissions.UPDATE_COMPANY_LTI}
                  onClick={() => setIsUploadModal(true)}
                  isLoading={isUploading}
                />

                <CustomButton
                  onClick={() => download()}
                  btnName="Download"
                  customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                  iconPosition="right"
                >
                  {/* <MdOutlineFileDownload
                    size={20}
                    className="text-white font-bold"
                  /> */}
                </CustomButton>
              </>
            ) : null}

            <CustomButton
              onClick={() => setIsVersionsModal(true)}
              btnName="Versions"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
              iconPosition="left"
            ></CustomButton>

            {checkPermission(permissions.CREATE_COMPANY_LTI) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName=" Create Long Term Incentive"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="left"
              ></CustomButton>
            )}

            <SearchBox
              placeholder="Type to search..."
              value={searchInput}
              onChange={value => setSearchInput(value)}
            />
            <div className="dropdown ml-4">
              <Dropdown
                overlay={columnOverlay}
                trigger={["click"]}
                placement="bottomLeft"
              // getPopupContainer={trigger => trigger.parentElement}
              >
                <CustomButton
                  btnName="Column Filter"
                  customClasses="shadow-md font-medium border py-1 px-4"
                >
                 <IoFilterSharp />
                </CustomButton>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>


      <div className="jobCreationListing mt-10 bg-white px-5 py-4 rounded-lg">
        {/* <div className="flex items-center justify-end gap-2">
          <SearchBox
            placeholder="Type to search..."
            value={searchInput}
            onChange={value => {
              setSearchInput(value);
              setPage(1);
            }}
          />
          <Dropdown
            overlay={columnOverlay}
            trigger={["click"]}
            placement="bottomLeft"
          // getPopupContainer={trigger => trigger.parentElement}
          >
            <CustomButton
              btnName="Columns"
              customClasses="shadow-md font-medium border py-1 px-4"
            >
             <IoFilterSharp />
            </CustomButton>
          </Dropdown>
        </div> */}
        <Row>
          <Table
            scroll={1500}
            data={data}
            columns={filteredColumns}
            isLoading={isLoading || isFetchingPlanIncentives}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            onPageSizeChange={setPageSize}
            page={page}
            onTableChange={(pagination, filters, sorter) => {
              if (sorter?.field) {
                setSortBy(sorter.field);
                setOrder(sorter.order === "ascend" ? "asc" : "desc");
                setPage(1);
              }
            }}
          />
        </Row>
      </div>
    </>
  );
};

export default LongTermIncentives;
